"use client";

import { clsx } from "@trydig/ui";
import { useImageLoaded } from "@/lib/hooks/useImageLoaded";

interface LqipProps {
	/** Tiny placeholder URL (Storyblok image-service, Centra mini, signed video thumb, etc.). */
	src: string;
	/** Default `"eager"`. Pass `"lazy"` for below-the-fold thumbnails. */
	loading?: "eager" | "lazy";
	/** Object-position when the LQIP needs to match a focal point. */
	objectPosition?: string;
	/** Fade-in duration in ms. Default 200. */
	durationMs?: number;
	/**
	 * Override visibility once loaded. Used for hover-swap cases where the
	 * second LQIP should stay hidden until the card is hovered, even after
	 * the tiny image has decoded. Default `true`.
	 */
	visible?: boolean;
}

/**
 * Absolutely-positioned blur-up placeholder. Renders as a plain `<img>` (not
 * `next/image`) so the bytes hit the browser without the optimizer round-trip,
 * applies a CSS blur to mask the low resolution, and fades in once the source
 * has decoded. `useImageLoaded` covers the cached-image case via a callback
 * ref that checks `img.complete` on mount.
 */
export const Lqip = ({
	src,
	loading = "eager",
	objectPosition,
	durationMs = 200,
	visible = true,
}: LqipProps) => {
	const [loaded, ref, onLoad] = useImageLoaded();
	const show = visible && loaded;

	return (
		// eslint-disable-next-line @next/next/no-img-element
		<img
			ref={ref}
			aria-hidden
			alt=""
			src={src}
			loading={loading}
			decoding="async"
			onLoad={onLoad}
			className={clsx(
				"absolute inset-0 h-full w-full object-cover pointer-events-none transition-opacity ease-out",
				show ? "opacity-100" : "opacity-0",
			)}
			style={{
				filter: "blur(16px) saturate(1.2)",
				objectPosition,
				transitionDuration: `${durationMs}ms`,
			}}
		/>
	);
};
